/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	OnDestroy,
	OnChanges,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import * as appStrings from '@touchplan/strings';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IListItemEventData } from '../interfaces/IListItemEventData';
import { UntypedFormGroup } from '@angular/forms';
import { IButtonPanelEventData } from '../interfaces/IButtonPanelEventData';
import { EditorMode } from './abstract-item-list-editor.component';
import { IListItemFormEventData } from '../dialog-reusable.module';
import { GenericDialogComponent } from '../../generic-dialog/generic-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { AutoHideLabelComponent } from 'src/app/common/utility-components/auto-hide-label/auto-hide-label.component';

@Component({
	selector: 'add-edit-item-base',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AbstractAddEditItemComponent<T> implements OnInit, OnDestroy, OnChanges {
	@Input() public item: T;
	@Input() public palette: string[][];
	@Input() public usedColors: object;
	@Input() public editorMode: EditorMode;
	@Input() public isAdd: boolean;
	@Output() public memberCommitEvent = new EventEmitter<IListItemEventData<T>>();
	@Output() public itemFormEvent = new EventEmitter<IListItemFormEventData<T>>();

	@ViewChild('autoHideLabel', { static: false }) public autoHideLabel: AutoHideLabelComponent;

	public strings = appStrings;
	protected shutdownSubject$ = new Subject<void>();
	public EditorMode = EditorMode;
	public itemFormGroup: UntypedFormGroup;

	constructor(protected dialog: MatDialog) {}

	public ngOnInit(): void {}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let type = 'create';
		if (!this.itemFormGroup) {
			this.itemFormGroup = this.createFormGroup(this.item);
		}
		// this only happens if this is the edit panel since the item for edit is selected by user interactively later after dialog is up
		if (changes?.item?.currentValue) {
			type = 'update';
			this.updateFormGroup(changes.item.currentValue);
		}
		//item editor event
		this.itemFormEvent.emit({
			type,
			item: this.item,
			form: this.itemFormGroup,
		});
	}

	protected abstract createFormGroup(item: T);

	protected abstract updateFormGroup(item: T);

	public handleItemEvent(emittedData: IButtonPanelEventData) {
		switch (emittedData.type) {
			case 'Cancel':
				this.itemFormGroup.reset();
				this.fireCommitEvent(emittedData);
				break;
			case 'Delete':
				this.dialog
					.open(GenericDialogComponent, {
						data: {
							title: appStrings.DELETE_DIALOG_TEXT,
							yesButtonText: appStrings.DELETE_DIALOG_YES,
							noButtonText: appStrings.DELETE_DIALOG_NO,
						},
						disableClose: true,
					})
					.afterClosed()
					.pipe(takeUntil(this.shutdownSubject$))
					.subscribe((actionDelete) => {
						if (actionDelete) {
							this.itemFormGroup.reset();
							this.fireCommitEvent(emittedData);
						}
					});
				break;
			case 'RESEND':
				this.autoHideLabel.show();
				this.fireCommitEvent(emittedData);
			default:
				this.fireCommitEvent(emittedData);
				break;
		}
	}

	protected fireCommitEvent(emittedData: IButtonPanelEventData) {
		this.memberCommitEvent.emit({
			type: emittedData.type,
			event: emittedData.event,
			data: this.itemFormGroup.getRawValue(),
			item: this.item,
			id: this.item && this.item['$id'] ? this.item['$id'] : '',
			form: this.itemFormGroup,
		});
	}

	public canSave(): boolean {
		return this.itemFormGroup && this.itemFormGroup.valid && this.itemFormGroup.dirty;
	}
}
